export const LOCKED_IDENTIFIER_FIELDS = [
  'name',
  'togId',
  'embTag',
  'recordId',
  'outsideEntryId',
  'embryoName',
] as const;

/**
 * Fields that must never be changed by a *bulk* edit (WI 37965).
 *
 * These are identity, ownership, audit and recip-heat values that downstream
 * reporting and traceability depend on. Single-record editing is unaffected —
 * this list only applies when more than one embryo is selected, matching how
 * Ova Drive restricts "change of multiple records".
 *
 * Names here are **form field** names. Some are renamed on the way to the API
 * (`donor` -> `donorId`, `sire` -> `sireId`); see `BULK_LOCKED_PAYLOAD_KEYS`.
 */
export const BULK_LOCKED_FIELDS = [
  // identity
  'name',
  'togId',
  'embTag',
  'recordId',
  'outsideEntryId',
  'embryoName',
  // parentage
  'donor',
  'sire',
  'sireName',
  // ownership
  'billingOwnerId',
  'recipOwnerId',
  // audit
  'createdByName',
  'creation',
  'modifiedByName',
  'modifiedOn',
  // recip heat history
  'scPreviousHeat',
  'scLastHeat',
] as const;

/**
 * Payload keys to drop after `normalizeForSubmit` has run.
 *
 * `normalizeForSubmit` unwraps `donor`/`sire` into `donorId`/`sireId` and
 * re-emits every date field, and it is spread *after* the form-level strip —
 * so filtering only on form names would let locked values back into the
 * request.
 */
export const BULK_LOCKED_PAYLOAD_KEYS = [
  ...BULK_LOCKED_FIELDS,
  'donorId',
  'sireId',
] as const;

export const REQUIRED_IMPLANT_FIELDS = [
  'calfType',
  'implantDate',
  'recipOwnerId',
  'clinicRanchHaulIn',
  'siteId',
] as const;

export const REVERSAL_REQUIRED_FIELDS = [
  'statusCode',
  'storageLocationId',
  'siteId',
] as const;

export const IMPLANTABLE_STATUSES = [1, 4] as const;

export const IMPLANT_TYPE = {
  'Weaned Calf': 100000000,
  'Live Calf': 100000001,
  'Purchased Pregnancy': 100000002,
  'Client Recip - TOG Transfer': 100000003,
  Clone: 100000004,
  Research: 100000005,
  Training: 100000006,
  GAP: 100000008,
  'Non-TOG Implant': 100000009,
  'DO NOT USE Client Recip Purchase Pregnancy - TOG Transfer': 100000010,
  'DO NOT USE Client Recip Purchase Pregnancy - Client Transfer': 100000011,
} as const;

export const EMBRYO_STATUS = {
  FROZEN: 1,
  IMPLANTED: 2,
  DISCARDED: 3,
  SHIPPED: 4,
  USED: 5,
  THAWED_AND_DISCARDED: 6,
  AI: 7,
  PER_PREG_PRICING_HOLD: 8,
} as const;

export const ACCOUNT_TYPE = {
  'TOG Corporate Account': 100000000,
  'Normal Account': 100000001,
  ProGentus: 100000002,
  'ProGentus Nucleus Herd': 100000003,
  'ProGentus Sales Account': 100000004,
  Commercial: 100000005,
  International: 100000006,
  'Progentus Terminal Beef': 100000007,
  'Progentus Beef Replacement': 100000008,
  'Progentus Dairy Replacement': 100000009,
  'Progentus Holding': 100000010,
  'Progentus International': 100000011,
};

export const STATUS_COMMENT_TAG_OPTIONS = [
  'Duplicate Record',
  'Data Entry Error',
  'Owner Request',
  'Transfer Complete',
  'Record Correction',
] as const;

export const THAW_METHOD = {
  'Conv Chuteside': 100000000,
  'Conv Outerlab': 100000001,
  'Conv Innerlab': 100000002,
  'Cutout/Reloaded': 100000003,
  DT: 100000004,
  Vitrification: 100000005,
};

export const PC_RES_OPTIONS = {
  Open: 100000000,
  Pregnant: 100000001,
  Compromised: 100000002,
  'Left the Herd': 100000003,
};

export const OUTSIDE_EMBRYO_GENERAL_FIELDS = [
  'name',
  'freshFrozen',
  'donor',
  'sire',
  'transferOwnerId',
  'breedId',
] as const;

export const OUTSIDE_EMBRYO_IMPLANT_FIELDS = [
  'implantDate',
  'clinicRanchHaulIn',
  'method',
  'thawByName',
  'transferQuantity',
  'transferSiteId',
  'implantTierOf',
  'deyardId',
  'recipientOwnerId',
  'loadDonorPartners',
] as const;

export const OUTSIDE_EMBRYO_FREEZING_FIELDS = [
  'freezeTechId',
  'recoveryFirmCode',
  'cane',
  'freezeTime',
  'freezeMethodId',
  'storageLocationId',
  'freezer',
  'tt',
  'creation',
  'receivedOn',
  'freezeComment',
  'freezeQuantity',
  'freezeOwnerId',
] as const;

export const OUTSIDE_EMBRYO_ADDITIONAL_FIELDS = [
  'createdByName',
  'creation',
  'modifiedByName',
  'modifiedOn',
  'recoveryFirmCode',
  'recoveryFirmName',
  'stateCode',
  'statusCode',
  'name',
] as const;

export const OUTSIDE_EMBRYO_EDITABLE_FIELDS = [
  'donor',
  'sire',
  'breedId',
  'name',
] as const;
